import { Component, computed, inject, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ThemeService } from '../../core/services/theme.service';
import { isAdminRole } from '../../core/utils/admin-role';

export type SidebarNavIcon =
  | 'avatar'
  | 'list'
  | 'person'
  | 'people'
  | 'reservados'
  | 'lightbulb'
  | 'gear'
  | 'home'
  | 'admin-admins'
  | 'admin-store'
  | 'admin-flag';

export interface SidebarNavItem {
  readonly route: string;
  readonly label: string;
  readonly icon: SidebarNavIcon;
  readonly exact?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);

  /**
   * Alternar tema no rodapé da barra lateral — só para admin. A área de admin
   * não tem navbar (ver `showNavbar` no MainLayout) nem passa pela página de
   * Configurações, que é onde o utilizador comum troca o tema.
   */
  readonly showThemeToggle = computed(() => isAdminRole(this.authService.currentUser()?.role));
  readonly isDarkTheme = computed(() => this.themeService.theme() === 'dark');

  toggleTheme(): void {
    this.themeService.toggle();
  }

  /** Caminho sem query — atualiza após cada navegação. */
  private readonly urlPath = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => this.router.url.split('?')[0]),
      startWith(this.router.url.split('?')[0]),
    ),
    { initialValue: this.router.url.split('?')[0] },
  );

  /**
   * Visitante a ver lista pública ou perfil de utilizador — só o logo na barra lateral.
   */
  readonly guestMinimalSidebar = computed(() => {
    if (this.authService.isAuthenticated()) {
      return false;
    }
    const path = this.urlPath() ?? '';
    if (/^\/wishlists\/[^/]+/.test(path)) {
      return true;
    }
    if (/^\/user\/[^/]+\/wishlists\/?$/.test(path)) {
      return true;
    }
    return false;
  });

  /**
   * Após clicar num item, força estado colapsado até o rato sair da barra
   * (evita ficar expandido por :hover + :focus-within).
   */
  readonly suppressExpand = signal(false);

  /** Emitted when user clicks logout; parent should show confirmation modal. */
  logoutRequested = output<void>();

  readonly userAvatar = computed(() => {
    const u = this.authService.currentUser();
    const name = u?.fullName?.trim() || u?.username || u?.email || '?';
    const initial = name.charAt(0).toUpperCase();
    const profilePictureUrl = u?.profilePictureUrl ?? null;
    return { name: initial, profilePictureUrl };
  });

  onLogoutClick(): void {
    this.logoutRequested.emit();
  }

  onNavItemClick(): void {
    this.suppressExpand.set(true);
    queueMicrotask(() => {
      const el = document.activeElement as HTMLElement | null;
      if (el?.closest('.sidebar')) {
        el.blur();
      }
    });
  }

  onSidebarMouseLeave(): void {
    this.suppressExpand.set(false);
  }

  private readonly userNavItems: readonly SidebarNavItem[] = [
    { route: '/wishlists', label: 'Minhas Listas', icon: 'avatar' },
    { route: '/friends', label: 'Amigos', icon: 'people' },
    { route: '/reservations', label: 'Reservado', icon: 'reservados' },
    { route: '/explore', label: 'Explorar', icon: 'lightbulb' },
    { route: '/settings', label: 'Configurações', icon: 'gear' },
  ];

  private readonly adminNavItems: readonly SidebarNavItem[] = [
    { route: '/admin', label: 'Início', icon: 'home', exact: true },
    { route: '/admin/administrators', label: 'Administradores', icon: 'admin-admins' },
    { route: '/admin/partner-stores', label: 'Lojas Parceiras', icon: 'admin-store' },
    { route: '/admin/users', label: 'Usuários', icon: 'people' },
    { route: '/admin/reports', label: 'Denúncias', icon: 'admin-flag' },
  ];

  readonly isAdmin = computed(() => isAdminRole(this.authService.currentUser()?.role));

  readonly navItems = computed(() => {
    if (this.guestMinimalSidebar()) {
      return [] as readonly SidebarNavItem[];
    }
    return this.isAdmin() ? this.adminNavItems : this.userNavItems;
  });
}
